import React, { memo, useCallback, useEffect, useRef, useState } from "react";

// propTypes
import PropTypes from "prop-types";

import { parseCssStringToObject } from "../../../../helper";
import {
  DEFAULT_BLOCK_CSS,
  POPUP_SECTIONS,
  PRODUCT_TO_OFFER,
  offerTypeUsesRuntimeResolution,
} from "../../../../constant/constant";
import { useApp } from "../../../../context/AppState";
import TextBlock from "./TextBlock";
import ButtonBlock from "./ButtonBlock";
import DraggableWrapper from "../../../Draggable";
import CoverImageBlock from "./CoverImageBlock";
import QuantityPickerBlock from "./QuantityPickerBlock";
import VariantPickerBlock from "./VariantPickerBlock";
import PriceBlock from "./PriceBlock";
import RatingBlock from "./RatingBlock";
import CustomHtmlBlock from "./CustomHtmlBlock";
import ChoiceBlock from "./ChoiceBlock";
import SubscriptionBlock from "./SubscriptionBlock";
import ProductDescriptionBlock from "../../../../layouts/checkout-extension/post-purchase/ProductDescriptionBlock";
import {
  DESCRIPTION_LAYOUT,
  getDescriptionDisplay,
} from "../../../../../shared/post-purchase-product-sections.js";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import SubscriptionPickerBlock from "./SubscriptionPickerBlock";
import cartService from '../../../../services/cart.service';
import { usePopup } from '../../../../hooks/usePopup';
import {
  shouldShowDummyProducts,
  generateDummyProducts,
  getDummyProductCount,
  padProductsForPreviewSlots,
  filterRealPreviewProducts,
} from '../../../../utils/dummyProductGenerator';
import { filterProductsByOosBehavior } from "../../../../../shared/product-offerability.js";
import { getOosBehavior } from "../../../../../shared/widget-market-stock.js";
import {
  ackSelectBlockToParent,
  parseSelectBlockMessage,
} from '../../../../utils/preview-selection-messages';
import {
  usesCatalogPopupLayout,
} from '../../../../popup-layouts/index.js';
import {
  isSectionVisible,
  isSectionVisibleForViewport,
} from '../../../../../shared/section-visibility.js';
import catalogFlowStyles from "./ProductBlockCatalogFlow.module.css";
import { getCoverImageCatalogSizes } from "../../../../../shared/product-block-display.js";

const ProductBlock = ({
  activeStep,
  popupData,
  popupState,
  setPopupState,
  section,
  index: parentIndex,
  handleClose,
  onProductsChange,
  layoutFlow = false,
}) => {
  const { appState, isMobile } = useApp();
  // fetchRandomProduct === fetchOfferProducts (legacy name): GraphQL first, /product/random fallback.
  const { fetchOfferProducts, fetchRandomProduct, isLoadingProducts } = usePopup();

  // Helper function to get product handle from ID if needed
  const getProductHandleFromId = async (productId) => {
    try {
      // This would need to be implemented based on your backend API
      // For now, we'll use the random API which can handle product IDs
      console.log('[ProductBlock] Getting handle for product ID:', productId);
      return null; // Placeholder - implement based on your API
    } catch (error) {
      console.error('[ProductBlock] Error getting product handle from ID:', error);
      return null;
    }
  };

  // Helper function to get current product data from URL
  const getCurrentProductData = async () => {
    try {
      const path = window.location.pathname;
      const match = path.match(/\/products\/([^\/]+)/);
      if (!match) {
        console.log('[ProductBlock] No product handle found in URL');
        return null;
      }
      
      const currentHandle = match[1];
      console.log('[ProductBlock] Current product handle:', currentHandle);
      
      const currentProduct = await cartService.fetchProductByHandle(currentHandle);
      console.log('[ProductBlock] Current product data:', currentProduct);
      
      return currentProduct;
    } catch (error) {
      console.error('[ProductBlock] Error fetching current product:', error);
      return null;
    }
  };

  const [selectedBlock, setSelectedBlock] = useState({});
  const swiperRef = useRef(null);
  const [isEnd, setIsEnd] = useState(false);
  const [isBeginning, setIsBeginning] = useState(true);
  const [product, setProduct] = useState([]);
  const [isLoadingProduct, setIsLoadingProduct] = useState(false);
  const hasFetchedProducts = useRef(false); // Track if products have been fetched for this popup
  const { pathname, origin } = window.location;
  const showRealtimeProduct = section?.setting?.show_realtime_product;
  const productsToOfferType =
    popupData?.generalSettings?.condition?.product?.products_to_offer?.type;
  const usesRuntimeOffer = offerTypeUsesRuntimeResolution(productsToOfferType);

  useEffect(() => {
    setIsEnd(isMobile ? product.length <= 1 : product.length <= 3);
    setIsBeginning(isMobile ? product.length <= 1 : product.length <= 3);
  }, [product, isMobile]);

  useEffect(() => {
    if (
      !appState?.is_preview_mode &&
      usesRuntimeOffer &&
      !section?.runtimeResolved
    ) {
      return;
    }

    const productsToOffer =
      popupData?.generalSettings?.condition?.product?.products_to_offer;
    const offerType = productsToOffer?.type;
    const slotCount = getDummyProductCount(popupData, activeStep);

    if (appState?.is_preview_mode) {
      if (shouldShowDummyProducts(offerType)) {
        setProduct(generateDummyProducts(slotCount));
        return;
      }

      if (offerType === PRODUCT_TO_OFFER.SPECIFIC_PRODUCT) {
        const realProducts = filterRealPreviewProducts(section?.products);
        setProduct(
          realProducts.length === 0
            ? generateDummyProducts(slotCount)
            : padProductsForPreviewSlots(realProducts, slotCount)
        );
        return;
      }
    }

    if (section?.products?.length) {
      const liveProducts = appState?.is_preview_mode
        ? section.products
        : filterProductsByOosBehavior(
            section.products,
            getOosBehavior(popupData?.generalSettings?.condition)
          );
      setProduct(liveProducts);
    }
  }, [
    activeStep,
    popupData?.generalSettings?.condition?.product?.products_to_offer,
    popupData?.generalSettings?.condition?.product?.products_to_offer_limit,
    section?.products,
    section?.runtimeResolved,
    appState?.is_preview_mode,
    usesRuntimeOffer,
  ]);

  useEffect(() => {
    if (typeof onProductsChange === "function") {
      onProductsChange(product);
    }
  }, [product, onProductsChange]);

  useEffect(() => {
    if (showRealtimeProduct) {
      const fetchProduct = async () => {
        try {
          // Extract handle from pathname (assuming /products/[handle])
          const handle = pathname.split('/').pop();
          const jsProductRes = await cartService.fetchProductByHandle(handle);
          setProduct([{ ...jsProductRes, sellingPlanGroups: jsProductRes?.selling_plan_groups }]);
        } catch (err) {
          console.log('%csrc/components/Preview/Draggable/Block/ProductBlock.jsx:65 err', 'color: #007acc;', err);
        }
      };

      fetchProduct();
    }
  }, [showRealtimeProduct]);

  // Product fetching logic based on products_to_offer type
  useEffect(() => {
    const fetchProductBasedOnType = async () => {
      // Check if we should show dummy products (in preview mode or for non-specific product types)
      const productsToOffer = popupData?.generalSettings?.condition?.product?.products_to_offer;
      const shouldShowDummy = appState?.is_preview_mode && shouldShowDummyProducts(productsToOffer?.type);
      
      if (shouldShowDummy) {
        // Show dummy products in preview mode or for non-specific product types
        const productCount = getDummyProductCount(popupData, activeStep);
        const dummyProducts = generateDummyProducts(productCount);
        setProduct(dummyProducts);
        return;
      }
      
      const shouldFetchOnLive =
        !appState?.is_preview_mode &&
        productsToOffer?.type &&
        popupState?.openPopup &&
        !hasFetchedProducts.current &&
        ((usesRuntimeOffer && !section?.runtimeResolved) ||
          (!usesRuntimeOffer && !product.length));

      if (shouldFetchOnLive) {
        try {
          hasFetchedProducts.current = true; // Mark as fetched
          setIsLoadingProduct(true);
          
          const { products_to_offer : productsToOffer, products_to_offer_limit : productCount, allowMultipleProducts } = popupData.generalSettings.condition.product;
          console.log('[ProductBlock] Products to offer config:', productsToOffer);
          
          if (!productsToOffer?.type) {
            console.log('[ProductBlock] Invalid products_to_offer configuration:', productsToOffer);
            throw new Error('Invalid products_to_offer configuration');
          }

          if (
            productsToOffer.type === PRODUCT_TO_OFFER.SPECIFIC_PRODUCT &&
            (!productsToOffer.value || !productsToOffer.value.length)
          ) {
            console.log('[ProductBlock] Missing specific product selection:', productsToOffer);
            throw new Error('Invalid products_to_offer configuration');
          }
          
          let selectedProduct = null;
          
          switch(productsToOffer.type) {
            case PRODUCT_TO_OFFER.SHOPIFY_RECOMMENDED:
            case PRODUCT_TO_OFFER.AI_RECOMMENDED: {
              console.log(
                "[ProductBlock] Fetching recommendations via random API:",
                productsToOffer.type
              );
              try {
                const currentProductForRec = await getCurrentProductData();
                if (currentProductForRec?.id) {
                  selectedProduct = await fetchRandomProduct(
                    {
                      ...productsToOffer,
                      value: [{ id: currentProductForRec.id }],
                    },
                    false,
                    productCount,
                    allowMultipleProducts
                  );
                }
              } catch (e) {
                console.error(
                  "[ProductBlock] Error fetching recommendation products",
                  e
                );
              }
              break;
            }
            case PRODUCT_TO_OFFER.TOP_SELLING_PRODUCTS:
              // Same resolver as other offers: GraphQL BEST_SELLING, then server fallback.
              selectedProduct = await fetchOfferProducts(
                productsToOffer,
                false,
                productCount,
                allowMultipleProducts
              );
              break;
            case PRODUCT_TO_OFFER.SPECIFIC_PRODUCT:
              // Direct fetch via fetchProductByHandle for specific products
              if (productsToOffer.value && productsToOffer.value.length > 0) {
                const productInfo = productsToOffer.value[0];
                
              if (productInfo.id) {
                  // For now, use the random API which handles product IDs
                  selectedProduct = await fetchRandomProduct(productsToOffer, false, productCount, allowMultipleProducts);
              }
            }
              break;
              
            // REALTIME CASES - Centralized Logic
            case PRODUCT_TO_OFFER.REALTIME_SAME_COLLECTION:
            case PRODUCT_TO_OFFER.REALTIME_SAME_VENDOR:
            case PRODUCT_TO_OFFER.REALTIME_SAME_TAGS:
            case PRODUCT_TO_OFFER.REALTIME_TITLE_CONTAINS:
            case PRODUCT_TO_OFFER.REALTIME_TITLE_STARTS_WITH:
            case PRODUCT_TO_OFFER.REALTIME_TITLE_ENDS_WITH:
              console.log('[ProductBlock] Processing realtime case:', productsToOffer.type);
              const currentProduct = await getCurrentProductData();
              if (currentProduct) {
                // Map realtime type to existing filter type and extract value
                const getRealtimeFilter = (type, product) => {
                  switch(type) {
                    case PRODUCT_TO_OFFER.REALTIME_SAME_COLLECTION:
                      return {
                        type: PRODUCT_TO_OFFER.REALTIME_SAME_COLLECTION,
                        value: product.collections?.[0]?.handle ? [{ handle: product.collections[0].handle }] : null
                      };
                    case PRODUCT_TO_OFFER.REALTIME_SAME_VENDOR:
                      return {
                        type: PRODUCT_TO_OFFER.REALTIME_SAME_VENDOR,
                        value: product.vendor ? [product.vendor] : null
                      };
                    case PRODUCT_TO_OFFER.REALTIME_SAME_TAGS:
                      return {
                        type: PRODUCT_TO_OFFER.REALTIME_SAME_TAGS,
                        value: product.tags || []
                      };
                    case PRODUCT_TO_OFFER.REALTIME_TITLE_CONTAINS:
                      return {
                        type: PRODUCT_TO_OFFER.REALTIME_TITLE_CONTAINS,
                        value: [product.title]
                      };
                    case PRODUCT_TO_OFFER.REALTIME_TITLE_STARTS_WITH:
                      return {
                        type: PRODUCT_TO_OFFER.REALTIME_TITLE_STARTS_WITH,
                        value: [product.title.split(' ')[0]] // First word
                      };
                    case PRODUCT_TO_OFFER.REALTIME_TITLE_ENDS_WITH:
                      return {
                        type: PRODUCT_TO_OFFER.REALTIME_TITLE_ENDS_WITH,
                        value: [product.title.split(' ').pop()] // Last word
                      };
                    default:
                      return null;
                  }
                };
                
                const filter = getRealtimeFilter(productsToOffer.type, currentProduct);
                if (filter && filter.value) {
                  console.log('[ProductBlock] Applying realtime filter:', filter);
                  try {
                    selectedProduct = await fetchRandomProduct({
                      ...productsToOffer,
                      type: filter.type,
                      value: filter.value
                    }, false, productCount, allowMultipleProducts);
                    console.log('[ProductBlock] Fetched realtime product:', selectedProduct?.title);
                  } catch (error) {
                    console.error('[ProductBlock] Error fetching realtime product:', error);
                    // Fallback to default product
                    selectedProduct = await fetchRandomProduct({ type: PRODUCT_TO_OFFER.SPECIFIC_PRODUCT, value: [] }, false, productCount, allowMultipleProducts);
                  }
                } else {
                  console.log('[ProductBlock] No valid filter value found for realtime case, using fallback');
                  // Fallback to default product
                  selectedProduct = await fetchRandomProduct({ type: PRODUCT_TO_OFFER.SPECIFIC_PRODUCT, value: [] }, false, productCount, allowMultipleProducts);
                }
              } else {
                console.log('[ProductBlock] No current product data available for realtime case, using fallback');
                // Fallback to default product
                selectedProduct = await fetchRandomProduct({ type: PRODUCT_TO_OFFER.SPECIFIC_PRODUCT, value: [] }, false, productCount, allowMultipleProducts);
              }
              break;
            case PRODUCT_TO_OFFER.SHOW_CURRENT_PRODUCT:
              try {
                const current = await getCurrentProductData();
                if (current?.id) {
                  const mapped = {
                    type: PRODUCT_TO_OFFER.SHOW_CURRENT_PRODUCT,
                    value: [{ id: current.id }],
                  };
                  selectedProduct = await fetchRandomProduct(mapped, false, 1, false);
                }
              } catch (e) {
                console.error('[ProductBlock] Error fetching current product for SHOW_CURRENT_PRODUCT', e);
              }
              break;
              
            case PRODUCT_TO_OFFER.PRODUCT_SPECIFIC_TITLE:
              // Call random API with title filter
              console.log('[ProductBlock] Fetching product by specific title:', productsToOffer.value);
              selectedProduct = await fetchRandomProduct(productsToOffer, false, productCount, allowMultipleProducts);
              console.log('[ProductBlock] Fetched product by title:', selectedProduct?.title);
              break;
              
            case PRODUCT_TO_OFFER.PRODUCT_SPECIFIC_TAG:
              // Call random API with tag filter
              console.log('[ProductBlock] Fetching product by specific tag:', productsToOffer.value);
              selectedProduct = await fetchRandomProduct(productsToOffer, false, productCount, allowMultipleProducts);
              console.log('[ProductBlock] Fetched product by tag:', selectedProduct?.title);
              break;
              
            case PRODUCT_TO_OFFER.PRODUCT_SPECIFIC_VENDOR:
              // Call random API with vendor filter
              console.log('[ProductBlock] Fetching product by specific vendor:', productsToOffer.value);
              selectedProduct = await fetchRandomProduct(productsToOffer, false, productCount, allowMultipleProducts);
              console.log('[ProductBlock] Fetched product by vendor:', selectedProduct?.title);
              break;
              
            case PRODUCT_TO_OFFER.PRODUCT_FROM_COLLECTION:
              // Call random API with collection filter
              console.log('[ProductBlock] Fetching product from collection:', productsToOffer.value);
              selectedProduct = await fetchRandomProduct(productsToOffer, false, productCount, allowMultipleProducts);
              console.log('[ProductBlock] Fetched product from collection:', selectedProduct?.title);
              break;
              
            default:
              // Fallback to random product
              console.log('[ProductBlock] Using default random product fetch');
              selectedProduct = await fetchRandomProduct(productsToOffer, false, productCount, allowMultipleProducts);
              console.log('[ProductBlock] Fetched default random product:', selectedProduct?.title);
              break;
          }
          
          if (selectedProduct) {
            console.log('[ProductBlock] Selected product:', selectedProduct);
            
            // Handle both single product and multiple products
            if (allowMultipleProducts && Array.isArray(selectedProduct)) {
              setProduct(
                appState?.is_preview_mode
                  ? selectedProduct
                  : filterProductsByOosBehavior(
                      selectedProduct,
                      getOosBehavior(popupData?.generalSettings?.condition)
                    )
              );
              console.log('[ProductBlock] Multiple products loaded for popup:', popupData._id, selectedProduct.length, 'products');
            } else {
              // Single product - wrap in array for consistency
              setProduct(
                appState?.is_preview_mode
                  ? [selectedProduct]
                  : filterProductsByOosBehavior(
                      [selectedProduct],
                      getOosBehavior(popupData?.generalSettings?.condition)
                    )
              );
              console.log('[ProductBlock] Single product loaded for popup:', popupData._id, selectedProduct.title);
            }
          } else {
            console.log('[ProductBlock] No product found, using default product data');
            // Use default product data from section.products if available
            if (section?.products && section.products.length > 0) {
              setProduct(
                appState?.is_preview_mode
                  ? section.products
                  : filterProductsByOosBehavior(
                      section.products,
                      getOosBehavior(popupData?.generalSettings?.condition)
                    )
              );
              console.log('[ProductBlock] Using default product data from section');
            } else {
              // If no section products, set empty array to show loading state
              setProduct([]);
              console.log('[ProductBlock] No default products available');
            }
          }
        } catch (err) {
          console.log('Error fetching product:', err);
          hasFetchedProducts.current = false; // Reset on error
        } finally {
          setIsLoadingProduct(false);
        }
      }
    };

    fetchProductBasedOnType();
  }, [
    popupState?.openPopup,
    appState?.is_preview_mode,
    popupData?.generalSettings?.condition?.product?.products_to_offer,
    popupData?._id,
    usesRuntimeOffer,
    section?.runtimeResolved,
    product.length,
  ]);

  // Reset fetch flag when popup closes
  useEffect(() => {
    if (!popupState?.openPopup) {
      hasFetchedProducts.current = false;
      console.log('[ProductBlock] Reset fetch flag for popup:', popupData?._id);
    }
  }, [popupState?.openPopup, popupData?._id]);

  // Generic method to update swiper state
  const updateSwiperState = (swiper, context = '') => {
    setIsEnd(swiper.isEnd);
    setIsBeginning(swiper.isBeginning);
  };

  // Reset button states when products change
  useEffect(() => {
    if (product?.length > 0) {
      setIsBeginning(true);
      setIsEnd(isMobile ? product.length <= 1 : product.length <= 3);
    }
  }, [product]);

  // Update button states when swiper is ready
  useEffect(() => {
    if (swiperRef.current && product?.length > 0) {
      updateSwiperState(swiperRef.current, 'Swiper ready');
    }
  }, [product, swiperRef.current]);

  const selectedBlockRef = useRef(selectedBlock);
  selectedBlockRef.current = selectedBlock;

  const handleBlockClick = useCallback(
    (type, subSectionIndex) => {
      if (!appState?.is_preview_mode) return;
      const prev = selectedBlockRef.current;
      if (prev.type === type && prev.index === subSectionIndex) return;
      ackSelectBlockToParent({
        type,
        index: parentIndex,
        subIndex: subSectionIndex,
      });
      setSelectedBlock({ type, index: subSectionIndex });
    },
    [appState?.is_preview_mode, parentIndex]
  );

  useEffect(() => {
    const onMessage = (event) => {
      const data = event?.data;
      if (!data || typeof data !== "object") return;

      if (data.event === "sectionCreated") {
        const { type, subIndex: subSectionIndex } = data;
        if (typeof subSectionIndex === "number" && subSectionIndex > -1) {
          setSelectedBlock({ type, index: subSectionIndex });
          handleBlockClick(type, subSectionIndex);
        }
        return;
      }

      const selection = parseSelectBlockMessage(data);
      if (!selection) return;
      if (selection.index !== parentIndex) return;
      if (typeof selection.subIndex !== "number" || selection.subIndex < 0) return;

      setSelectedBlock({
        type: selection.type,
        index: selection.subIndex,
      });
      ackSelectBlockToParent({
        type: selection.type,
        index: parentIndex,
        subIndex: selection.subIndex,
      });
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [parentIndex, handleBlockClick]);


  const blockCSS = {
    ...DEFAULT_BLOCK_CSS,
    ...section?.setting?.customCSS?.[isMobile ? "mobile" : "desktop"],
    ...parseCssStringToObject(
      section?.setting?.overrideCss?.[isMobile ? "mobile" : "desktop"]
    ),
  };
  const usesStackedCatalogLayout =
    layoutFlow === true ||
    section?.setting?.layoutFlow === true ||
    usesCatalogPopupLayout(popupData);

  // Swiper columns must not inherit the full PRODUCT shell pixel width (e.g. ~990px)
  // per slide — that causes three slides to overlap. Fill each slide only.
  const slideShellStyle =
    product?.length > 1
      ? (() => {
          const s = {
            ...blockCSS,
            boxSizing: "border-box",
            position: "relative",
          };
          delete s.width;
          delete s.minWidth;
          delete s.maxWidth;
          s.width = "100%";
          s.maxWidth = "100%";
          s.minWidth = 0;
          s.height = "100%";
          s.overflow = "hidden";
          return s;
        })()
      : blockCSS;

  const productSectionsSwitch = useCallback(
    (
      productData,
      productIndex,
      productSection,
      productSectionIndex,
      { catalogFlow = false } = {}
    ) => {
      switch (productSection?.type) {
        case POPUP_SECTIONS.TITLE:
          return (
            <TextBlock
              popupData={popupData}
              activeStep={activeStep}
              section={productSection}
              parentData={productData}
              catalogFlow={catalogFlow}
            />
          );
        case POPUP_SECTIONS.SUBSCRIPTION:
          return (
            <SubscriptionBlock
              section={productSection}
              productIndex={productIndex}
              productData={productData}
            />
          );
        case POPUP_SECTIONS.CHOICE:
          return <ChoiceBlock section={productSection} />;
        case POPUP_SECTIONS.CUSTOM_HTML:
          return <CustomHtmlBlock section={productSection} parentData={productData} />;
        case POPUP_SECTIONS.DESCRIPTION: {
          const descriptionDisplay = getDescriptionDisplay(productSection);
          if (descriptionDisplay.layout === DESCRIPTION_LAYOUT.COLLAPSIBLE) {
            return (
              <ProductDescriptionBlock
                section={productSection}
                productData={productData}
              />
            );
          }
          return (
            <TextBlock
              popupData={popupData}
              activeStep={activeStep}
              section={productSection}
              parentData={productData}
              catalogFlow={catalogFlow}
            />
          );
        }
        case POPUP_SECTIONS.TEXT:
          if (
            usesStackedCatalogLayout &&
            productSection?.setting?.role === "productTagline"
          ) {
            return null;
          }
          return <TextBlock section={productSection} parentData={productData} />;
        case POPUP_SECTIONS.RATING:
          if (usesStackedCatalogLayout && !catalogFlow) return null;
          return (
            <RatingBlock section={productSection} catalogFlow={catalogFlow} />
          );
        case POPUP_SECTIONS.COVER_IMAGE:
          return (
            <CoverImageBlock
              section={productSection}
              productIndex={productIndex}
              productData={productData}
              popupData={popupData}
              activeStep={activeStep}
            />
          );
        case POPUP_SECTIONS.BUTTON:
          return (
            <ButtonBlock
              popupData={popupData}
              popupState={popupState}
              setPopupState={setPopupState}
              section={productSection}
              index={productSectionIndex}
              productIndex={productIndex}
              productData={productData}
              handleClose={handleClose}
            />
          );
        case POPUP_SECTIONS.QUANTITY_PICKER:
          return (
            <QuantityPickerBlock
              section={productSection}
              index={productSectionIndex}
              productIndex={productIndex}
              popupData={popupData}
              activeStep={activeStep}
            />
          );
        case POPUP_SECTIONS.VARIANT_PICKER:
          return (
            <VariantPickerBlock
            popupData={popupData}
              section={productSection}
              index={productSectionIndex}
              productIndex={productIndex}
              productData={productData}
            />
          );
        case POPUP_SECTIONS.SUBSCRIPTION_PICKER:
          return (
            <SubscriptionPickerBlock
              section={productSection}
              index={productSectionIndex}
              productIndex={productIndex}
              productData={productData}
            />
          );
        case POPUP_SECTIONS.PRICE:
          return (
            <PriceBlock
              productData={productData}
              section={productSection}
              index={productSectionIndex}
              productIndex={productIndex}
              popupData={popupData}
            />
          );
        // case POPUP_SECTIONS.BADGE:
        //   return <BadgeBlock text="New Arrival" section={productSection} />;
        default:
          return null;
      }
    },
    [
      popupData,
      popupState,
      setPopupState,
      section,
      usesStackedCatalogLayout,
      activeStep,
    ]
  );

  // Generic method for navigation button styles
  const getNavigationButtonStyle = (disabled, position) => ({
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: "10",
    background: disabled ? "#e0e0e0" : "white",
    opacity: disabled ? "0.8" : "1",
    padding: "0",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    outline: "none",
    transition: "background 0.2s ease",
    borderRadius: "50%",
    border: "1px solid gray",
    [position]: "0"
  });

  // Generic method for button hover effects
  const handleButtonHover = (e, disabled) => {
    e.currentTarget.style.background = disabled ? "#e0e0e0" : "#f5f5f5";
  };

  const handleButtonLeave = (e, disabled) => {
    e.currentTarget.style.background = disabled ? "#e0e0e0" : "white";
  };

  const isSliderVerical =
  popupData?.schema[activeStep]?.popupSettings?.slider_direction ===
  "vertical";
  // Generic method to handle swiper navigation
  const handleSwiperNavigation = (direction) => {
    if (swiperRef.current) {
      const method = direction === 'next' ? 'slideNext' : 'slidePrev';
      swiperRef.current[method]();
    }
  };

  const handlePrev = () => handleSwiperNavigation('prev');
  const handleNext = () => handleSwiperNavigation('next');

  // Generic navigation button component
  const NavigationButton = ({ direction, disabled, onClick }) => {
    const isPrev = direction === 'prev';
    const iconPath = isPrev ? "M15 18l-6-6 6-6" : "M9 18l6-6-6-6";
    const position = isPrev ? "left" : "right";
    
    return (
      <button
        disabled={disabled}
        onClick={onClick}
        onMouseEnter={(e) => handleButtonHover(e, disabled)}
        onMouseLeave={(e) => handleButtonLeave(e, disabled)}
        style={getNavigationButtonStyle(disabled, position)}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="gray"
          strokeWidth="2"
        >
          <path d={iconPath} />
        </svg>
      </button>
    );
  };

  // Generic swiper configuration
  const swiperConfig = {
    isVertical: isSliderVerical,
    showNavButtons: isMobile ? product?.length > 1 : product?.length > 3,
    slidesPerView: isMobile
      ? 1
      : Math.min(3, Math.max(1, product?.length || 1)),
    spaceBetween: 16,
  };

  const getSubBlockCss = useCallback(
    (productSection) => ({
      ...DEFAULT_BLOCK_CSS,
      ...productSection?.setting?.customCSS?.[isMobile ? "mobile" : "desktop"],
      ...parseCssStringToObject(
        productSection?.setting?.overrideCss?.[isMobile ? "mobile" : "desktop"]
      ),
    }),
    [isMobile]
  );

  const getSectionByType = useCallback(
    (type) => section?.sections?.find((s) => s?.type === type),
    [section?.sections]
  );

  const renderFlowSubSection = useCallback(
    (productData, productIndex, productSection, productSectionIndex, inlineStyle = {}) => {
      if (!productSection) return null;
      if (
        !isSectionVisible(productSection) ||
        !isSectionVisibleForViewport(productSection, isMobile)
      ) {
        return null;
      }
      const css = getSubBlockCss(productSection);
      if (css?.display === "none") return null;
      const isSubSelected =
        appState?.is_preview_mode &&
        selectedBlock?.type === productSection?.type &&
        selectedBlock?.index === productSectionIndex;
      return (
        <div
          key={`${parentIndex}-${productIndex}-${productSectionIndex}`}
          style={{
            width: "100%",
            boxSizing: "border-box",
            ...inlineStyle,
            ...(isSubSelected
              ? { outline: "2px solid #ea580c", outlineOffset: 2 }
              : {}),
          }}
          onClick={() =>
            handleBlockClick(productSection?.type, productSectionIndex, productIndex)
          }
        >
          {productSectionsSwitch(
            productData,
            productIndex,
            productSection,
            productSectionIndex,
            { catalogFlow: true }
          )}
        </div>
      );
    },
    [
      appState?.is_preview_mode,
      getSubBlockCss,
      handleBlockClick,
      isMobile,
      parentIndex,
      productSectionsSwitch,
      selectedBlock?.index,
      selectedBlock?.type,
    ]
  );

  return (
    <>
      {/* Show loading indicator when fetching products */}
      {isLoadingProduct && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          width: '100%',
          flexDirection: 'column',
          gap: '15px',
          padding: '20px',
          // backgroundColor: '#f8f9fa',
          // borderRadius: '8px',
          // border: '1px solid #e9ecef'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <div style={{ fontSize: '14px', color: '#666' }}>
            Loading products...
          </div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}

      {/* Only show products when not loading */}
      {!isLoadingProduct && (product?.length > 1 ? (
        <Swiper
          spaceBetween={swiperConfig.spaceBetween}
          direction={swiperConfig.isVertical ? "vertical" : "horizontal"}
          slidesPerView={swiperConfig.slidesPerView}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
            updateSwiperState(swiper, 'Swiper initialized');
          }}
          onSlideChange={(swiper) => updateSwiperState(swiper, 'onSlideChange')}
          pagination={{ clickable: !appState?.is_preview_mode }}
          style={{
            position: "relative",
            width: "100%",
            height:
              section?.setting?.dimension?.[isMobile ? "mobile" : "desktop"]
                ?.height,
            ...(swiperConfig.isVertical ? {} : { overflowX: "clip" }),
            overflowY: "visible",
          }}
        >
          {!swiperConfig.isVertical && swiperConfig.showNavButtons && (
            <>
              <NavigationButton 
                direction="prev" 
                disabled={isBeginning} 
                onClick={handlePrev} 
              />
              <NavigationButton 
                direction="next" 
                disabled={isEnd} 
                onClick={handleNext} 
              />
            </>
          )}
          {product?.map((productData, productIndex) => {

            return (
              <>
                <SwiperSlide
                  key={productIndex}
                  style={{
                    ...(!isSliderVerical && { height: "100%", width: "100%" }),
                     zIndex:`${product?.length * 2 - productIndex }`,
                  }}
                >
                  <div style={slideShellStyle}>
                    {section?.sections?.map(
                      (productSection, productSectionIndex) => {
                        if (!isSectionVisible(productSection)) return null;
                        return (
                          <DraggableWrapper
                            key={`${parentIndex}-${productIndex}-${productSectionIndex}`}
                            index={parentIndex}
                            popupSettings={
                              popupData?.schema[activeStep]?.popupSettings
                            }
                            lockDraggable={
                              popupData?.generalSettings?.lockDraggable
                            }
                            section={productSection}
                            isSelected={
                              productSection?.type === selectedBlock?.type &&
                              productSectionIndex === selectedBlock?.index
                            }
                            onClick={() =>
                              handleBlockClick(
                                productSection?.type,
                                productSectionIndex,
                                productIndex
                              )
                            }
                            isSubSection
                            subIndex={productSectionIndex}
                            sectionLockDraggable={
                              productSection?.setting?.isLockDraggable
                            }
                            layoutFlow={usesStackedCatalogLayout}
                          >
                            {productSectionsSwitch(
                              productData,
                              productIndex,
                              productSection,
                              productSectionIndex
                            )}
                          </DraggableWrapper>
                        );
                      }
                    )}
                  </div>
                </SwiperSlide>
              </>
            );
          })}
        </Swiper>
      ) : usesStackedCatalogLayout ? (
        <div style={blockCSS}>
          {product?.map((productData, productIndex) => {
            const imageSection = getSectionByType(POPUP_SECTIONS.COVER_IMAGE);
            const titleSection = getSectionByType(POPUP_SECTIONS.TITLE);
            const ratingSection = getSectionByType(POPUP_SECTIONS.RATING);
            const descSection = getSectionByType(POPUP_SECTIONS.DESCRIPTION);
            const priceSection = getSectionByType(POPUP_SECTIONS.PRICE);
            const variantSection = getSectionByType(POPUP_SECTIONS.VARIANT_PICKER);
            const quantitySection = getSectionByType(POPUP_SECTIONS.QUANTITY_PICKER);

            const titleIndex = section?.sections?.findIndex(
              (s) => s?.type === POPUP_SECTIONS.TITLE
            );
            const ratingIndex = section?.sections?.findIndex(
              (s) => s?.type === POPUP_SECTIONS.RATING
            );
            const descIndex = section?.sections?.findIndex(
              (s) => s?.type === POPUP_SECTIONS.DESCRIPTION
            );
            const imageIndex = section?.sections?.findIndex(
              (s) => s?.type === POPUP_SECTIONS.COVER_IMAGE
            );
            const priceIndex = section?.sections?.findIndex(
              (s) => s?.type === POPUP_SECTIONS.PRICE
            );
            const variantIndex = section?.sections?.findIndex(
              (s) => s?.type === POPUP_SECTIONS.VARIANT_PICKER
            );
            const qtyIndex = section?.sections?.findIndex(
              (s) => s?.type === POPUP_SECTIONS.QUANTITY_PICKER
            );

            const flowGap = "var(--ai-upsell-section-gap, 10px)";
            const imageSizes = getCoverImageCatalogSizes(imageSection, isMobile);

            return (
              <div key={`${parentIndex}-${productIndex}-flow`} style={{ width: "100%" }}>
                <div
                  style={{
                    display: "flex",
                    gap: flowGap,
                    alignItems: "flex-start",
                    width: "100%",
                    marginBottom: flowGap,
                  }}
                >
                  <div
                    style={{
                      width: imageSizes.columnWidth,
                      minWidth: imageSizes.columnWidth,
                      maxWidth: imageSizes.columnWidth,
                      flexShrink: 0,
                    }}
                  >
                    {renderFlowSubSection(
                      productData,
                      productIndex,
                      imageSection,
                      imageIndex,
                      {
                        minHeight: imageSizes.mainHeight,
                        overflow: "hidden",
                        borderRadius: "var(--ai-upsell-border-radius, 14px)",
                      }
                    )}
                  </div>
                  <div
                    className={catalogFlowStyles.catalogTextColumn}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                      alignItems: "flex-start",
                    }}
                  >
                    {renderFlowSubSection(
                      productData,
                      productIndex,
                      titleSection,
                      titleIndex
                    )}
                    {renderFlowSubSection(
                      productData,
                      productIndex,
                      ratingSection,
                      ratingIndex,
                      { marginTop: 2 }
                    )}
                    {renderFlowSubSection(
                      productData,
                      productIndex,
                      priceSection,
                      priceIndex,
                      { marginTop: 6, marginBottom: 4 }
                    )}
                    {renderFlowSubSection(
                      productData,
                      productIndex,
                      variantSection,
                      variantIndex,
                      { marginTop: 4, marginBottom: 4 }
                    )}
                    {renderFlowSubSection(
                      productData,
                      productIndex,
                      quantitySection,
                      qtyIndex,
                      { marginTop: 4 }
                    )}
                  </div>
                </div>
                {renderFlowSubSection(
                  productData,
                  productIndex,
                  descSection,
                  descIndex,
                  {
                    width: "100%",
                    marginTop: 2,
                  }
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div style={blockCSS}>
          {product?.map((productData, productIndex) => {
            return (
              <>
                {section?.sections?.map(
                  (productSection, productSectionIndex) => {
                    if (!isSectionVisible(productSection)) return null;
                    return (
                      <DraggableWrapper
                        key={`${parentIndex}-${productIndex}-${productSectionIndex}`}
                        index={parentIndex}
                        popupSettings={
                          popupData?.schema[activeStep]?.popupSettings
                        }
                        section={productSection}
                        isSelected={
                          productSection?.type === selectedBlock?.type &&
                          productSectionIndex === selectedBlock?.index
                        }
                        onClick={() =>
                          handleBlockClick(
                            productSection?.type,
                            productSectionIndex,
                            productIndex
                          )
                        }
                        isSubSection
                        subIndex={productSectionIndex}
                        lockDraggable={
                          popupData?.generalSettings?.lockDraggable
                        }
                        sectionLockDraggable={
                          productSection?.setting?.isLockDraggable
                        }
                        layoutFlow={usesStackedCatalogLayout}
                      >
                        {productSectionsSwitch(
                          productData,
                          productIndex,
                          productSection,
                          productSectionIndex
                        )}
                      </DraggableWrapper>
                    );
                  }
                )}
              </>
            );
          })}
        </div>
      ))}


    </>
  );
};

ProductBlock.propTypes = {
  section: PropTypes.object,
};

export default ProductBlock;
